/**
 * MetadataDiffDialog — compact before/after preview for the semantic
 * model CSV metadata import. Called with the dry-run response, lets the
 * user review every changed field grouped by entity, spot validation
 * errors, and decide whether to apply.
 */
import React, { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertTriangle, ArrowRight, Loader2, FileText, ChevronRight, ChevronDown } from 'lucide-react';

// Human-readable label for the metadata fields we care about.
const FIELD_LABEL = {
  display_name: 'Display name',
  description: 'Description',
  role: 'Role',
  aggregation: 'Aggregation',
  format: 'Format',
  formula: 'Formula',
};

function diffFields(before, after) {
  const keys = new Set([...Object.keys(before || {}), ...Object.keys(after || {})]);
  const rows = [];
  keys.forEach((k) => {
    const b = before?.[k];
    const a = after?.[k];
    if ((b ?? null) === (a ?? null)) return;
    rows.push({ key: k, before: b, after: a });
  });
  return rows;
}

export function MetadataDiffDialog({
  open, onOpenChange, dryRun, applying, onApply,
}) {
  const [expanded, setExpanded] = useState({});

  // Group changes by entity for compact rendering.
  const grouped = useMemo(() => {
    const g = new Map();
    (dryRun?.changes || []).forEach((c) => {
      const entity = c.kind === 'calc_measure' ? '__calc_measures__' : (c.entity_name || 'Unknown');
      if (!g.has(entity)) g.set(entity, []);
      g.get(entity).push(c);
    });
    return g;
  }, [dryRun]);

  const totalChanges = dryRun?.total_changes ?? 0;
  const errors = dryRun?.errors || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <FileText className="w-4 h-4 text-primary" />
            Review metadata changes
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-2 flex-wrap" data-testid="metadata-diff-summary">
          <Badge className="text-[10px] bg-primary/15 text-primary border-primary/25">
            {totalChanges} field {totalChanges === 1 ? 'change' : 'changes'}
          </Badge>
          <Badge className="text-[10px] bg-muted text-foreground/80">
            {grouped.size} {grouped.size === 1 ? 'entity' : 'entities'}
          </Badge>
          {errors.length > 0 && (
            <Badge className="text-[10px] bg-destructive/15 text-destructive border-destructive/25 gap-1">
              <AlertTriangle className="w-3 h-3" />
              {errors.length} skipped {errors.length === 1 ? 'row' : 'rows'}
            </Badge>
          )}
        </div>

        <ScrollArea className="max-h-[60vh] pr-4 -mr-4">
          {totalChanges === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground" data-testid="metadata-diff-empty">
              No field-level changes found in the uploaded CSV.
            </div>
          ) : (
            <div className="space-y-2">
              {Array.from(grouped.entries()).map(([entity, changes]) => {
                const isOpen = expanded[entity] !== false; // default open
                const title = entity === '__calc_measures__' ? 'Calculated measures' : entity;
                return (
                  <div key={entity} className="rounded-lg border border-border/50 overflow-hidden" data-testid={`metadata-diff-entity-${entity}`}>
                    <button
                      type="button"
                      className="w-full flex items-center justify-between px-3 py-2 bg-muted/20 hover:bg-muted/30 transition-colors"
                      onClick={() => setExpanded((prev) => ({ ...prev, [entity]: !isOpen }))}
                      data-testid={`metadata-diff-entity-toggle-${entity}`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        {isOpen ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />}
                        <span className="text-xs font-semibold text-foreground truncate">{title}</span>
                      </div>
                      <span className="text-[10px] text-muted-foreground">{changes.length} field{changes.length === 1 ? '' : 's'}</span>
                    </button>
                    {isOpen && (
                      <div className="divide-y divide-border/30">
                        {changes.map((c, idx) => {
                          const fieldDiffs = diffFields(c.before, c.after);
                          return (
                            <div key={`${entity}-${c.field_name}-${idx}`} className="px-3 py-2" data-testid={`metadata-diff-field-${c.field_name}`}>
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-mono text-[11px] text-foreground">{c.field_name}</span>
                                <span className="text-[9px] text-muted-foreground uppercase tracking-wider">{c.kind === 'calc_measure' ? 'measure' : 'attribute'}</span>
                              </div>
                              <div className="grid grid-cols-1 gap-1">
                                {fieldDiffs.map((d) => (
                                  <div key={d.key} className="grid grid-cols-[110px_1fr_auto_1fr] items-center gap-2 text-[10px]">
                                    <span className="text-muted-foreground">{FIELD_LABEL[d.key] || d.key}</span>
                                    <span className="font-mono text-destructive/80 line-through truncate" title={String(d.before ?? '')}>
                                      {formatValue(d.before)}
                                    </span>
                                    <ArrowRight className="w-3 h-3 text-muted-foreground/80" />
                                    <span className="font-mono text-emerald-400 truncate" title={String(d.after ?? '')}>
                                      {formatValue(d.after)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}

              {errors.length > 0 && (
                <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 mt-3" data-testid="metadata-diff-errors">
                  <div className="flex items-center gap-2 mb-1">
                    <AlertTriangle className="w-3.5 h-3.5 text-destructive" />
                    <span className="text-xs font-semibold text-destructive">{errors.length} row{errors.length === 1 ? '' : 's'} will be skipped</span>
                  </div>
                  <ul className="space-y-0.5">
                    {errors.slice(0, 8).map((e, i) => (
                      <li key={i} className="text-[10px] text-destructive/85 font-mono">{e}</li>
                    ))}
                    {errors.length > 8 && (
                      <li className="text-[10px] text-destructive/70">… and {errors.length - 8} more</li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          )}
        </ScrollArea>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={applying} data-testid="metadata-diff-cancel">
            Cancel
          </Button>
          <Button
            onClick={onApply}
            disabled={applying || totalChanges === 0}
            className={errors.length > 0 ? 'bg-amber-500 hover:bg-amber-600 text-black' : ''}
            data-testid="metadata-diff-apply"
          >
            {applying ? <><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> Applying…</> : `Apply ${totalChanges} change${totalChanges === 1 ? '' : 's'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function formatValue(v) {
  if (v === undefined || v === null || v === '') return <span className="text-muted-foreground italic">empty</span>;
  const str = typeof v === 'string' ? v : JSON.stringify(v);
  return str.length > 60 ? str.slice(0, 57) + '…' : str;
}

export default MetadataDiffDialog;
